import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  IconBuilding,
  IconStore,
  type TabItem,
  rovingKeyHandler,
} from '../../../ui/v2';
import {
  type Experience,
  experienceLanding,
  useExperiences,
} from './experiences';

/**
 * Restaurant ⇄ Hotel, at the top of both sidebars (components doc §1).
 *
 * A two-segment radiogroup, not a menu: two options, and the selected
 * segment doubles as the "which experience am I in" label. Renders nothing
 * (no DOM node) for single-mode users and while the experiences are still
 * resolving, so the sidebar never shows a control that then disappears.
 *
 * Sits on the navy column inside a `.v2-inverse` block, so the selected tint
 * is whatever `--v2-accent` is there: indigo in the restaurant sidebar, teal
 * in the hotel one (`.v2-nav-hotel`, tokens.css). The legacy restaurant
 * sidebar (`?frame=legacy`) renders it with `surface="page"`.
 */
export type { Experience };

const SEGMENTS: Array<TabItem<Experience> & { icon: React.ReactNode }> = [
  { key: 'restaurant', label: 'Restaurant', icon: <IconStore size={14} /> },
  { key: 'hotel', label: 'Hotel', icon: <IconBuilding size={14} /> },
];

export interface ExperienceSwitcherProps {
  /** Which shell is rendering it. Drives the selected segment. */
  current: Experience;
  /** Called before navigation; the drawer uses it to close. */
  onNavigate?: () => void;
  /**
   * `nav` (default) sits on the navy V2 column; `page` keeps page tokens,
   * for the legacy restaurant sidebar's light/dark surface.
   */
  surface?: 'nav' | 'page';
  className?: string;
}

/**
 * Switching unmounts the shell that holds the focused segment, so focus is
 * handed to the destination's switcher when it mounts. Short-lived, so a
 * switcher that only appears later (a collapsed legacy sidebar on hover)
 * never takes focus.
 */
let arrival: { experience: Experience; at: number } | null = null;
const ARRIVAL_MS = 1_000;

/** False under a `display: none` ancestor. Browsers without the API: true. */
const isRendered = (element: HTMLElement) =>
  typeof element.checkVisibility !== 'function' || element.checkVisibility();

export const ExperienceSwitcher: React.FC<ExperienceSwitcherProps> = ({
  current,
  onNavigate,
  surface = 'nav',
  className,
}) => {
  const { hasRestaurant, hasHotel, pending } = useExperiences();
  const { user } = useAuth();
  const navigate = useNavigate();
  const groupRef = useRef<HTMLDivElement>(null);
  const shown = !pending && hasRestaurant && hasHotel;

  useEffect(() => {
    if (!shown || arrival?.experience !== current) return;
    const fresh = Date.now() - arrival.at < ARRIVAL_MS;
    arrival = null;
    if (!fresh) return;
    const segment = groupRef.current?.querySelector<HTMLElement>(
      `[data-key="${current}"]`,
    );
    // Below 1024px this is the pinned copy, mounted but `display: none`, and
    // the drawer that held the other shell's copy has closed. Hand focus to
    // the page rather than losing it to <body>; its menu button is next.
    if (segment && isRendered(segment)) segment.focus();
    else groupRef.current?.closest('.av2')?.querySelector('main')?.focus();
  }, [shown, current]);

  if (!shown) return null;

  const choose = (next: Experience) => {
    if (next === current) return;
    onNavigate?.();
    arrival = { experience: next, at: Date.now() };
    // That experience's last-visited page, else its landing. Never a reload:
    // hotel queries are keyed by group and restaurant ones by store, so there
    // is nothing to reset (IA §3). The shells record `av2.experience`.
    navigate(experienceLanding(next, user?.id));
  };

  // Roving radiogroup, the `Segmented` contract: only the selected segment is
  // a tab stop; arrows and Home/End move selection, which here navigates.
  const onKeyDown = rovingKeyHandler(SEGMENTS, current, choose, {
    next: ['ArrowRight', 'ArrowDown'],
    prev: ['ArrowLeft', 'ArrowUp'],
  });

  return (
    <div
      className={clsx(
        // Outside any `.av2` frame, the page variant brings its own tokens.
        surface === 'nav'
          ? 'v2-inverse px-3 pb-3 pt-1'
          : 'av2 bg-transparent pb-4',
        className,
      )}
    >
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label="Experience"
        onKeyDown={onKeyDown}
        className="flex h-9 w-full items-stretch rounded-v2 border border-v2-line bg-v2-surface/60 p-0.5"
      >
        {SEGMENTS.map((segment) => {
          const selected = segment.key === current;
          return (
            <button
              key={segment.key}
              data-key={segment.key}
              role="radio"
              type="button"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => choose(segment.key)}
              className={clsx(
                'flex flex-1 items-center justify-center gap-2 rounded-[8px] text-v2-12 transition-colors',
                selected
                  ? 'bg-v2-accent/20 font-semibold text-v2-fg'
                  : 'font-medium text-v2-fg-2 hover:bg-v2-sunken/70 hover:text-v2-fg',
              )}
            >
              <span
                aria-hidden
                className={clsx(
                  'shrink-0',
                  selected ? 'text-v2-accent' : 'text-v2-fg-3',
                )}
              >
                {segment.icon}
              </span>
              {segment.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
